import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {container} from "tsyringe";
import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";

// Side-effect import: registers <o2d-whiteboard>. This popup does NOT go
// through knowledge-whiteboard.component.ts (deliberately — see the class
// doc comment below), so it must not depend on that component having been
// loaded first.
import "@o2d/whiteboard";
import "@o2d/whiteboard/whiteboard-canvas.css";

/**
 * Readonly popup viewer for a `KnowledgeObject` of type `whiteboard` (Anke,
 * 2026-09-04): "readonly variante ohne bearbeitungselemente... in einem
 * popup" — opened by a Linkview tile's `_open()`
 * (`link-tile-knowledge-object.component.ts`). Deliberately a NEW, thin
 * component rather than reusing `knowledge-whiteboard.component.ts`: that one
 * bakes in a Save button, image-paste upload, and dirty-tracking — all
 * editing machinery this popup must NOT have ("nur die Dateien anzeigen,
 * ohne das ganze Drumherum").
 *
 * `<o2d-whiteboard readonly>` blocks drag/resize/edit-mode entry, click- and
 * keyboard-selection, and self-hides its options-circle. Its own toolbar is
 * suppressed via `linkview-view.css` (`ha-dialog-whiteboard-view
 * o2d-whiteboard-toolbar { display: none }`) — the package would hide the
 * drawing tools itself, but this popup wants no toolbar at all, not just no
 * tools.
 *
 * Bis @o2d/whiteboard 0.5.0 stand hier ein eigener Workaround: Damals war
 * `InteractionController.onStageClick` ausdrücklich NICHT readonly-gesperrt —
 * der Docblock dort beschränkte den readonly-Vertrag auf Stage-DOWN-Gesten
 * (drag-to-create, marquee-start) —, und ein Klick zeigte deshalb den vollen
 * Konva-Transformer samt Griffen, die als Canvas-Zeichnung auch per CSS nicht
 * wegzubekommen sind. Ein `_onBoardMount` fing dafür `mousedown` in der
 * CAPTURE-Phase ab, solange `board.isPanArmed` false war, damit Mausrad-Zoom
 * und Leertaste+Ziehen weiter funktionieren (Anke, 2026-09-05: "die
 * Tastenkürzel für Bewegung... mit einzubauen").
 *
 * Mit 0.6.0 sperrt das Paket diesen Weg selbst (Review 0023: Guard in
 * `onStageClick`, und der Transformer trägt bei `readonly` keine Greifpunkte
 * mehr). Der Workaround ist deshalb entfernt — er verhinderte nur noch, was
 * das Paket ohnehin verhindert, und seine Begründung stimmte nicht mehr.
 * Schwenken und Zoomen bleiben unberührt: das sind Ansichts-, keine
 * Bearbeitungsvorgänge.
 *
 * Reihenfolge, damit niemand stolpert: `frontend/package.json` verlangt hier
 * noch `@o2d/whiteboard ^0.5.0`. Live spielt das keine Rolle, weil vite alle
 * `@o2d/*` auf die Quellen des Paket-Checkouts umbiegt — die Sperre ist also
 * schon aktiv. Der Bereich wird erst angehoben, wenn 0.6.0 tatsächlich
 * veröffentlicht ist; vorher bräche ein `npm ci`.
 *
 * Structured like the other non-entity dialogs (`AbstractDialogComponent`
 * directly, `_close()` via `DialogHelper.closeDialog()` + CLOSE command —
 * never `this.close()` directly, which would leave `DialogHelper`'s
 * `_openedDialog` lock set).
 */
@customElement('ha-dialog-whiteboard-view')
export class HaDialogWhiteboardViewComponent extends AbstractDialogComponent {

    @property({type: String})
    public uid = '';

    @state()
    private _config: readonly unknown[] = [];

    @state()
    private _isReady = false;

    /** Monotonic counter guarding against a stale `findOneByIdWithContent`
     *  resolving after `uid` has already changed — `DialogHelper` reuses the
     *  SAME dialog instance while one of this type is already open
     *  (`Object.assign(dialog, payload)` + `.open()`), so a second tile click
     *  can change `uid` mid-request. Mirrors `knowledge-whiteboard.
     *  component.ts`'s identical guard. */
    private _loadGeneration = 0;

    public constructor() {
        super();
        this.closable = true;
    }

    /**
     * `protected`, as Lit declares it — the tests reach it through a small
     * subclass that exposes it (see the spec's `TestableDialog`), rather than
     * this widening its visibility for their convenience.
     */
    protected async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has('uid') || !this.uid) {
            return;
        }

        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        this._isReady = false;

        let knowledgeObject;
        try {
            knowledgeObject = await container.resolve(KnowledgeObjectRepository).findOneByIdWithContent(this.uid);
        } catch (error) {
            // Review Code-Lauf 01, MAJOR: without this, a backend/network
            // failure became an unhandled rejection and left the dialog
            // stuck on the empty loading placeholder forever, with no
            // feedback — unlike knowledge-whiteboard.component.ts's identical
            // call (try/catch/finally) and ha-dialog-link-tile-edit's picker
            // load, which this was meant to mirror but didn't.
            if (myGeneration !== this._loadGeneration || !this.isConnected) return;
            console.error('Failed to load whiteboard for the readonly popup', error);
            UiHelper.notify({
                level: 'error',
                title: 'Fehler beim Laden',
                content: html`<p>Das Whiteboard konnte nicht geladen werden.</p>`,
                closable: true,
            });
            this._close();
            return;
        }
        if (myGeneration !== this._loadGeneration || !this.isConnected) {
            // A newer load started, or the dialog was closed while this was
            // in flight — discard; acting now could resurrect an already-
            // closed popup or overwrite a newer uid's content.
            return;
        }

        // `findOneByIdWithContent` itself does NOT filter on `deletedAt`
        // (unlike `findAll`) — the protection here comes entirely from the
        // backend's own default soft-delete filter. Documented so nobody
        // "symmetrically" adds a client-side deletedAt check here assuming
        // one already existed.
        if (!knowledgeObject || knowledgeObject.type !== 'whiteboard') {
            UiHelper.notify({
                level: 'error',
                title: 'Nicht gefunden',
                content: html`<p>Das verknüpfte Whiteboard existiert nicht mehr.</p>`,
                closable: true,
            });
            this._close();
            return;
        }

        this.title = knowledgeObject.title;
        this._config = knowledgeObject.content?.payload ?? [];
        this._isReady = true;
    }

    protected _renderMainContent(): HTMLTemplateResult {
        if (!this._isReady) {
            return html`<div class="ha-dialog-whiteboard-view__loading"></div>`;
        }

        return html`
            <o2d-whiteboard readonly .config=${this._config}></o2d-whiteboard>
        `;
    }


    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    private _close(): void {
        DialogHelper.closeDialog(this.tagName);
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0,
        });
    }

}
